import { useMemo } from 'react';
import { bucketSearchQuery } from '../charts/drilldown';
import { TrendChart } from '../charts/TrendChart';
import { navigate } from '../router';
import { EmptyState, Section } from '../ui';
import type { RateTrend } from './trend';

/**
 * 최근 1시간 초당 요청·오류 (22-B회차, 빠진 정보 5).
 *
 * 계산은 `RateTrend`, 그림은 `TrendChart`다. 여기서는 언제 다시 계산할지만 정한다 —
 * 스냅샷이 올 때마다 `version`이 오르고 그때만 계열을 다시 만든다.
 *
 * 23-A회차부터 구간을 누르면 그 1분의 요청 검색으로 간다(UI 피드백 17).
 * 초당 오류 선을 눌렀으면 상태=오류까지 건다. 서비스 필터도 함께 넘긴다.
 */

/** 계열 순서. 초당 오류 선을 누르면 상태=오류까지 건다 */
const ERROR_SERIES = 1;
export function RateTrendSection({
  trend,
  version,
  service,
  now,
}: {
  trend: RateTrend;
  /** 스토어의 `trendVersion`. 이 값이 바뀔 때만 다시 계산한다 */
  version: number;
  /** 서비스 필터. 비면 전체 */
  service: string;
  /** 시험용. 없으면 지금 */
  now?: number;
}) {
  const chart = useMemo(
    () => trend.series(now ?? Date.now(), service === '' ? undefined : service),
    // version은 다시 계산할 때를 정하는 값이다
    [trend, version, service, now],
  );
  const hasData = chart.series[0]?.values.some((value) => value !== undefined) === true;

  return (
    <Section
      title="최근 1시간 초당 요청·오류"
      note={[
        '1초 보고를 1분 단위로 모은 초당 평균.',
        '관제 서버가 들고 있는 30분치는 새로고침해도 남고, 그 앞은 이 화면을 열어 둔 동안만 쌓인다',
      ]}
    >
      <div className="rounded-xl border border-hair bg-panel p-3">
        {hasData ? (
          <TrendChart
            buckets={chart.buckets}
            bucketMs={trend.bucketMs}
            series={chart.series}
            label="최근 1시간 초당 요청·오류. 1분 단위 초당 평균"
            onPick={(pick) => {
              navigate({
                name: 'requests',
                query: bucketSearchQuery(
                  pick.bucketStart,
                  trend.bucketMs,
                  { errorsOnly: pick.series === ERROR_SERIES, service },
                  // 마지막 구간은 아직 흐르는 중이다. 지금까지로 자른다
                  { to: now ?? Date.now() },
                ),
              });
            }}
          />
        ) : (
          <EmptyState
            title="아직 모인 값이 없다"
            hint="인스턴스가 보고를 시작하면 1분 뒤부터 그려진다"
          />
        )}
      </div>
    </Section>
  );
}
